// [src/pages/MyPage/MyPage.jsx]

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { getTokenStatus, clearAllAuthStorage } from 'utils/tokenManager';
import { API_BASE_URL } from 'config/apiConfig';

// CSS imports
import 'styles/Font.css';

// Image imports
import profileImage from "assets/images/icon/profile-image-icon.svg";
import arrowRight from "assets/images/icon/arrow-right-icon.svg";
import crownIcon from "assets/images/icon/crown-icon.svg"

// Components imports
import Footer from "components/layout/Footer/Footer";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
import NotYetModal from "components/ui/Modals/NotYetModal/NotYetModal";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import { useForegroundRefetch } from 'hooks/useForegroundRefetch';


const formatPhoneNumber = (rawPhoneNumber) => {
    if (!rawPhoneNumber) return '';
    const cleaned = ('' + rawPhoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3,4})(\d{4})$/);
    if (match) {
        return match[1] + '-' + match[2] + '-' + match[3];
    }
    return rawPhoneNumber;
};

// 잔여 기간 계산 (edate 기준, Boost.jsx의 동일 로직과 통일)
const calculateRemainDays = (edate) => {
    if (!edate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cleaned = edate.replace(/[^0-9]/g, '');
    if (cleaned.length !== 8) return null;

    const year = parseInt(cleaned.substring(0, 4));
    const month = parseInt(cleaned.substring(4, 6)) - 1;
    const day = parseInt(cleaned.substring(6, 8));
    const endDate = new Date(year, month, day);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : null;
};

function MyPage() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();
    const navigate = useNavigate();

    const [userInfo, setUserInfo] = useState(null);
    // const [paymentStatus, setPaymentStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [profileImgSrc, setProfileImgSrc] = useState(profileImage);
    const [subscriptionName, setSubscriptionName] = useState(null);
    const [subscriptionRemainDays, setSubscriptionRemainDays] = useState(null);
    const [subscriptionRemainCount, setSubscriptionRemainCount] = useState(null);
    const [subscriptionPath, setSubscriptionPath] = useState(null);

    const [isNotYetModalOpen, setIsNotYetModalOpen] = useState(false);
    const [notYetMessage, setNotYetMessage] = useState("");

    const handleCloseNotYetModal = () => {
        setIsNotYetModalOpen(false);
        setNotYetMessage("");
    };

    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

    const handleLogoutClick = () => {
        setIsLogoutModalOpen(true);
    };

    const handleConfirmLogout = () => {
        setIsLogoutModalOpen(false);
        clearAllAuthStorage();
        navigate('/');
    };

    const handleCancelLogout = () => {
        setIsLogoutModalOpen(false);
    };

    const handleMyReviewClick = () => {
        navigate('/my/reviews');
    };

    const handleAttendanceClick = () => {
        navigate('/my/attendance');
    };
    const handleProfileClick = () => {
        navigate('/my/profile');
    };

    // --- 데이터 로드 ---
    const fetchMyData = useCallback(async () => {
        const { token, isExpired } = getTokenStatus();

        if (!token || isExpired) {
            clearAllAuthStorage();
            setIsLoading(false);
            navigate('/', { replace: true });
            return;
        }

        const storedInfoStr = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
        // const storedPayment = sessionStorage.getItem('paymentStatus');

        if (storedInfoStr) {
            try {
                const parsedInfo = JSON.parse(storedInfoStr);
                setUserInfo(parsedInfo);
                // setPaymentStatus(storedPayment);
                if (parsedInfo.userImg) updateProfileImage(parsedInfo.userImg);
            } catch (e) {
                console.error(e);
            }
        }

        try {
            const response = await fetch(`${API_BASE_URL}/userInfo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                const latestInfo = data.data?.userInfo || {};

                setUserInfo(latestInfo);
                updateProfileImage(latestInfo.userImg || latestInfo.img || latestInfo.profileImage);
                sessionStorage.setItem('userInfo', JSON.stringify(latestInfo));
            }
        } catch (error) {
            console.error("MyPage info fetch error:", error);
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    // 구독 정보 조회
    const fetchSubscriptionInfo = useCallback(async () => {
        const { token } = getTokenStatus();
        if (!token) return;

        // 잔여 횟수 계산 (totalCnt/useCnt가 모두 유효한 숫자일 때만, 기간권이면 null)
        const calcRemainCount = (info) => {
            const total = Number(info.totalCnt);
            const used = Number(info.useCnt);
            if (!Number.isFinite(total) || !Number.isFinite(used) || total <= 0) return null;
            return total - used;
        };

        const applySubscription = (info, path) => {
            setSubscriptionName(info.name || '');
            setSubscriptionRemainDays(calculateRemainDays(info.edate));
            setSubscriptionRemainCount(calcRemainCount(info));
            setSubscriptionPath(path);
        };

        const clearSubscription = () => {
            setSubscriptionName(null);
            setSubscriptionRemainDays(null);
            setSubscriptionRemainCount(null);
            setSubscriptionPath(null);
        };

        try {
            const resL = await fetch(`${API_BASE_URL}/oneOrder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams({ pdKind: 'L' })
            });
            const dataL = await resL.json();
            // [버그수정] 서버 응답은 { info: {...} } 키로 감싸져 오는데 'userInfo'로 잘못 읽고 있어
            // 항상 undefined가 되어 실제 구독 중이어도 '일반권'으로만 표시되던 문제. (2026-08-21)
            const infoL = dataL.data?.info || dataL.info;

            if (infoL && infoL.odKey) {
                applySubscription(infoL, '/sb');
                return;
            }

            const resC = await fetch(`${API_BASE_URL}/oneOrder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams({ pdKind: 'C' })
            });
            const dataC = await resC.json();
            const infoC = dataC.data?.info || dataC.info;

            if (infoC && infoC.odKey) {
                applySubscription(infoC, '/pt');
                return;
            }

            clearSubscription();

        } catch (e) {
            console.error("Subscription Fetch Error:", e);
            clearSubscription();
        }
    }, []);

    // 포그라운드 진입 시 데이터 최신화
    const handleRefetch = useCallback(() => {
        fetchMyData();
        fetchSubscriptionInfo();
    }, [fetchMyData, fetchSubscriptionInfo]);

    useForegroundRefetch(handleRefetch);

    useEffect(() => {
        fetchMyData();
    }, [fetchMyData]);

    useEffect(() => {
        fetchSubscriptionInfo();
    }, [fetchSubscriptionInfo]);

    const updateProfileImage = (serverPath) => {
        if (!serverPath || (typeof serverPath === 'object' && Object.keys(serverPath).length === 0)) {
            setProfileImgSrc(profileImage);
            return;
        }

        if (typeof serverPath === 'string' && serverPath.trim() !== '') {
            if (serverPath.startsWith('http')) {
                setProfileImgSrc(serverPath);
            } else {
                const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
                const path = serverPath.startsWith('/') ? serverPath : `/${serverPath}`;
                setProfileImgSrc(`${baseUrl}${path}`);
            }
        } else {
            setProfileImgSrc(profileImage);
        }
    };

    if (isLoading) {
        return (
            <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                height: '100dvh', background: '#0D042D', color: 'white'
            }}>
                <p>내 정보 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>

            <main style={staticStyles.mainContent}>
                <div>
                    <img className="profileImage" src={profileImgSrc} alt="프로필 이미지" style={staticStyles.profileImage} />
                </div>

                <div className="userInfo" style={staticStyles.userInfo}>
                    <div className="userNameDiv" style={staticStyles.userNameDiv} typeof='button'
                        onClick={handleProfileClick}>
                        <span className="userName" style={staticStyles.userName}>
                            {userInfo?.userName || '사용자 이름'}
                        </span>
                        <div className="editDiv" style={staticStyles.editDiv} onClick={handleProfileClick}>
                            <img className="edit" src={arrowRight} alt="수정" style={staticStyles.editIcon} />
                        </div>
                    </div>
                    <span className="userId" style={staticStyles.userId}>
                        {formatPhoneNumber(userInfo?.phoneNumber || userInfo?.hp || userInfo?.phone || userInfo?.userId) || '-    -'}
                    </span>
                </div>

                {/* 이용중인 상품 카드: 실제 구독 중일 때만 표시. 기간권은 상품명/잔여기간,
                    횟수권은 상품명/잔여기간/잔여횟수 순으로 표시 */}
                {subscriptionName && (
                    <button
                        className="Subscription"
                        style={staticStyles.subscriptionButton}
                        onClick={() => subscriptionPath && navigate(subscriptionPath)}
                    >
                        <div className="crownDiv" style={staticStyles.crownDiv}>
                            <img className="crownIcon" src={crownIcon} alt="crown" style={staticStyles.crownIcon} />
                            <span className="subscriptionText" style={staticStyles.subscriptionText}>
                                {subscriptionName}
                            </span>
                        </div>
                        <div className="subscriptionDetailDiv" style={staticStyles.subscriptionDetailDiv}>
                            <span style={staticStyles.subscriptionDetailText}>
                                잔여기간 {subscriptionRemainDays !== null ? `${subscriptionRemainDays}일` : '-'}
                            </span>
                            {subscriptionRemainCount !== null && (
                                <span style={staticStyles.subscriptionDetailText}>
                                    잔여횟수 {subscriptionRemainCount}회
                                </span>
                            )}
                        </div>
                    </button>
                )}

                <div className="myMenuDiv" style={staticStyles.myMenuDiv}>
                    <div className="usageInfo" style={staticStyles.menuSection}>
                        <p className="usageInfoTitle" style={staticStyles.menuTitle}>이용 정보</p>
                        <div className="usageInfoContent" style={staticStyles.menuContentBox}>
                            <button className="attendanceHistory" style={staticStyles.menuButton}
                                onClick={handleAttendanceClick}>
                                <span style={staticStyles.menuButtonText}>출석 내역</span>
                                <img className="arrowRight" src={arrowRight} alt="arrow"
                                    style={staticStyles.arrowRightIcon} />
                            </button>

                            <div className="horizonBar" style={staticStyles.horizonBar} />

                            <button className="myReview" style={staticStyles.menuButton}
                                onClick={handleMyReviewClick}>
                                <span style={staticStyles.menuButtonText}>내 리뷰</span>
                                <img className="arrowRight" src={arrowRight} alt="arrow"
                                    style={staticStyles.arrowRightIcon} />
                            </button>
                        </div>
                    </div>

                    <div className="paymentDiv" style={staticStyles.menuSection}>
                        <p className="paymentTitle" style={staticStyles.menuTitle}>결제</p>
                        <div className="paymentContent" style={staticStyles.menuContentBox}>
                            <button className="paymentHistory" style={staticStyles.menuButton}
                                onClick={() => navigate('/payment')}>
                                <span style={staticStyles.menuButtonText}>결제 내역</span>
                                <img className="paymentArrowRight" src={arrowRight} alt="arrow"
                                    style={staticStyles.arrowRightIcon} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="accountDiv" style={staticStyles.menuSection}>
                    <p className="accountTitle" style={staticStyles.menuTitle}>계정</p>
                    <div className="accountContent" style={staticStyles.menuContentBox}>
                        <button className="logoutButton" style={staticStyles.menuButton}
                            onClick={handleLogoutClick}>
                            <span style={staticStyles.menuButtonText}>로그아웃</span>
                        </button>
                    </div>
                </div>

                <Footer />
            </main>

            <ConfirmModal isOpen={isLogoutModalOpen} onClose={handleCancelLogout} onConfirm={handleConfirmLogout}
                title="로그아웃" message="정말로 로그아웃 하시겠습니까?" />
            <NotYetModal isOpen={isNotYetModalOpen} onClose={handleCloseNotYetModal} message={notYetMessage} />

        </div>
    );
}

const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    mainContent: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '19px',
        padding: '0 24px 100px 24px',
    },
    horizonBar: { width: '100%', height: '1px', background: '#3A394A', },
    profileImage: {
        width: '54px',
        height: '54px',
        marginTop: '10px',
        borderRadius: '50%',
        objectFit: 'cover',
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        color: '#9A9A9A',
        marginTop: '-13px',
    },
    userNameDiv: { display: 'flex', alignItems: 'center', gap: '4px', },
    userName: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '22px', color: '#FFFFFF', lineHeight: '17px', },
    editDiv: { width: '24px', height: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', },
    editIcon: { width: '7px', height: '14px', border: 'none', color: '#F5F8F9', },
    userId: { fontFamily: 'Pretendard', fontWeight: 300, fontSize: '14px', color: '#9A9A9A', },
    subscriptionButton: {
        width: '100%',
        borderRadius: '8px',
        background: 'linear-gradient(90deg, #8052FF 10%, #0231FB 90%)',
        border: 'none',
        padding: '16px 22px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        cursor: 'pointer',
        boxSizing: 'border-box',
    },
    crownDiv: { display: 'flex', alignItems: 'center', gap: '4px', },
    crownIcon: { width: '20px', height: '18px', color: '#FCFCFA', },
    subscriptionText: {
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', color: '#FFFFFF', lineHeight: '21px',
    },
    subscriptionDetailDiv: { display: 'flex', alignItems: 'center', gap: '12px', },
    subscriptionDetailText: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '13px', color: '#E0E0E0', lineHeight: '18px',
    },
    arrowRightIcon: { width: '7px', height: '14px', border: 'none', color: '#F5F8F9' },
    myMenuDiv: { width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' },
    menuSection: { width: '100%', color: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: '12px' },
    menuTitle: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '20px', color: '#FFFFFF', margin: 0 },
    menuContentBox: {
        width: '100%',
        background: '#1B1931',
        borderRadius: '10px',
        paddingLeft: '22px',
        paddingRight: '22px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    },
    menuButton: {
        width: '100%',
        height: '57px',
        background: "transparent",
        border: 'none',
        textAlign: 'left',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 0,
        cursor: 'pointer',
    },
    menuButtonText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '16px', color: '#FFFFFF', textAlign: 'left' },
    bottomNavWrapper: { flexShrink: 0 },
};

export default MyPage;/**/